import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { FileText, Cpu, FileSignature, Plus, Menu, RefreshCcw, Download, Settings, LogOut, FileEdit, ClipboardCheck, ShieldAlert, CheckCircle2, Send } from 'lucide-react';
import ClientPicker from '../../../components/ClientPicker';
import PdfPreviewModal from '../../../components/PdfPreviewModal';
import api, { certificatesAPI, clientsAPI, tenantUsersAPI, testEquipmentAPI } from '../../../api';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../hooks/useToast';
import TextInputWithSuggestions from '../../../components/ui/TextInputWithSuggestions';
import CircuitsManager from './CircuitsManager';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';

const TABS = [
  { key: 'installation_details', label: 'Installation Details', Icon: FileText },
  { key: 'circuits', label: 'Circuits', Icon: Cpu },
  { key: 'declaration', label: 'Declaration', Icon: FileSignature },
  { key: 'appendix', label: 'Appendix', Icon: FileText },
];

const STATUS_OPTIONS = [
  {
    value: 'draft',
    label: 'Draft / In progress',
    description: 'Working copy visible to internal team members.',
    Icon: FileEdit,
  },
  {
    value: 'ready_for_qs',
    label: 'Ready for QS',
    description: 'Hand over to QS for review and approval.',
    Icon: ClipboardCheck,
  },
  {
    value: 'in_qs',
    label: 'In QS',
    description: 'Currently under QS review.',
    Icon: ShieldAlert,
    requiresPermission: true,
  },
  {
    value: 'complete',
    label: 'Complete',
    description: 'QS has approved and the certificate is complete.',
    Icon: CheckCircle2,
    requiresPermission: true,
  },
  {
    value: 'sent_to_client',
    label: 'Sent to client',
    description: 'Certificate issued to the client portal or via email.',
    Icon: Send,
  },
];

const PREMISES_OPTIONS = [
  'Domestic',
  'Commercial',
  'Industrial',
  'Agricultural',
  'Educational',
  'Medical',
];

const PRESET_STORAGE_KEY = 'mewcFieldPresets';

const PRESET_FIELD_CONFIG = {
  extentCovered: { label: 'Extent covered', placeholder: 'Describe the extent of the installation...' },
  generalCondition: { label: 'General condition of the installation', placeholder: 'Describe the general condition...' },
  bs7671Departures: { label: 'Details of departures from BS 7671, as amended', placeholder: 'Enter details of any departures from BS 7671...' },
};

const DEFAULT_FIELD_PRESETS = Object.keys(PRESET_FIELD_CONFIG).reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {});

const EMPTY_CIRCUITS = {
  name: '',
  location: '',
  zsAtDb: '',
  ipfAtDb: '',
  spdStatus: '',
  notes: '',
  multifunctionMft: '',
  continuity: '',
  insulationResistance: '',
  earthLoop: '',
  rcd: '',
};

const EQUIPMENT_TYPE_MAP = {
  Multifunction: 'multifunctionMft',
  Continuity: 'continuity',
  'Insulation Resistance': 'insulationResistance',
  'Earth loop': 'earthLoop',
  RCD: 'rcd',
};

const EMPTY_EQUIPMENT_GROUP = {
  multifunctionMft: [],
  continuity: [],
  insulationResistance: [],
  earthLoop: [],
  rcd: [],
};

const createEmptySignatory = () => ({
  id: '',
  label: '',
  type: '',
  value: '',
});

const parseSignatory = (input, fallbackTenantId = '') => {
  if (!input) return createEmptySignatory();

  if (typeof input === 'string') {
    const label = input.trim();
    const safeLabel = label || '';
    return {
      id: '',
      label: safeLabel,
      type: 'legacy',
      value: safeLabel ? `legacy:${safeLabel}` : '',
    };
  }

  if (typeof input === 'object') {
    const label = (input.label || input.name || '').trim();
    const id = input.id || input.userId || '';
    const rawValue = input.value || '';
    const derivedType = input.type
      || (rawValue.startsWith('tenant:') ? 'tenant'
        : rawValue.startsWith('user:') ? 'user'
          : id ? 'user'
            : label ? 'legacy'
              : '');

    let value = rawValue;
    if (!value) {
      if (derivedType === 'tenant') {
        const tenantRef = id || fallbackTenantId || 'current';
        value = `tenant:${tenantRef}`;
      } else if (derivedType === 'user' && id) {
        value = `user:${id}`;
      } else if (label) {
        value = `legacy:${label}`;
      }
    }

    return {
      id: id || '',
      label,
      type: derivedType,
      value,
    };
  }

  return createEmptySignatory();
};

const serializeSignatory = (signatory) => {
  if (!signatory) return null;
  const label = (signatory.label || '').trim();
  const id = signatory.id ? String(signatory.id) : '';
  const value = signatory.value || (signatory.type === 'tenant'
    ? `tenant:${id || ''}`
    : signatory.type === 'user' && id
      ? `user:${id}`
      : label
        ? `legacy:${label}`
        : '');
  const type = signatory.type || (value.startsWith('tenant:') ? 'tenant' : value.startsWith('user:') ? 'user' : label ? 'legacy' : '');

  if (!label && !id && !value) return null;

  return {
    id: id || null,
    label,
    type,
    value,
  };
};

const mergeOptionsWithLegacy = (options, signatory) => {
  const base = Array.isArray(options) ? [...options] : [];
  if (!signatory || !signatory.value) {
    return base;
  }

  const alreadyPresent = base.some((opt) => opt.value === signatory.value);
  if (alreadyPresent) {
    return base;
  }

  const baseLabel = signatory.label || signatory.value.replace(/^legacy:/, '') || 'Legacy selection';
  base.push({
    value: signatory.value,
    label: baseLabel.endsWith(' (legacy)') ? baseLabel : `${baseLabel} (legacy)`,
    type: signatory.type || 'legacy',
    disabled: true,
  });
  return base;
};

const createPresetId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `preset-${Math.random().toString(36).slice(2, 11)}-${Date.now().toString(36)}`;
};

export default function NewMewc() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const { success, error: showError, ToastContainer } = useToast();
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  const userRole = useAuthStore((state) => state.user?.role);
  const currentUser = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('installation_details');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);
  const [certificateStatus, setCertificateStatus] = useState('draft');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const canManageRestrictedStatuses = useMemo(() => {
    if (userRole === 'global_admin') return true;
    return Boolean(currentUser?.isAdmin || currentUser?.isQs);
  }, [currentUser, userRole]);
  const currentStatusOption = useMemo(
    () => STATUS_OPTIONS.find((option) => option.value === certificateStatus) || STATUS_OPTIONS[0],
    [certificateStatus]
  );

  const handleOpenStatusModal = () => {
    setIsHeaderMenuOpen(false);
    setStatusError('');
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    if (statusUpdating) return;
    setIsStatusModalOpen(false);
    setStatusError('');
  };

  const handleStatusSelect = async (newStatus) => {
    if (!newStatus) return;
    setStatusError('');

    if (newStatus === certificateStatus) {
      setIsStatusModalOpen(false);
      return;
    }

    const selectedOption = STATUS_OPTIONS.find((option) => option.value === newStatus);
    if (selectedOption?.requiresPermission && !canManageRestrictedStatuses) {
      setStatusError('Only a QS or Admin can set the status to "In QS" or "Complete".');
      return;
    }

    try {
      setStatusUpdating(true);
      if (serverId) {
        await certificatesAPI.update(serverId, { status: newStatus });
      }
      setCertificateStatus(newStatus);
      saveDraft(undefined, newStatus);
      setIsStatusModalOpen(false);
    } catch (err) {
      setStatusError(err?.message || 'Failed to update certificate status.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const [signatoryOptions, setSignatoryOptions] = useState([]);
  const [signatoriesLoading, setSignatoriesLoading] = useState(false);

  // Basic form state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientId, setClientId] = useState('');
  const [uid, setUid] = useState('');
  const [uidLocked, setUidLocked] = useState(false);
  const [serverId, setServerId] = useState(editId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [dateCreated, setDateCreated] = useState(() => new Date().toISOString().slice(0, 10));
  const [installationAddress, setInstallationAddress] = useState('');
  const [useClientAddress, setUseClientAddress] = useState(false);
  const [premises, setPremises] = useState('');
  const [minorWorksDate, setMinorWorksDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [earthingArrangement, setEarthingArrangement] = useState('');

  // Extent & limitations
  const [extentCovered, setExtentCovered] = useState('');
  const [generalCondition, setGeneralCondition] = useState('');
  const [fieldPresets, setFieldPresets] = useState(() => ({ ...DEFAULT_FIELD_PRESETS }));
  const [presetDrawerOpen, setPresetDrawerOpen] = useState(false);
  const [activePresetField, setActivePresetField] = useState(null);
  const [presetForm, setPresetForm] = useState({ name: '', content: '' });
  const [presetError, setPresetError] = useState('');
  const [inspectedBy, setInspectedBy] = useState(() => createEmptySignatory());
  const [inspectedDate, setInspectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [authorisedBy, setAuthorisedBy] = useState(() => createEmptySignatory());
  const [authorisedDate, setAuthorisedDate] = useState('');

  // Declaration details
  const [bs7671Departures, setBs7671Departures] = useState('');
  const [riskAssessmentAttached, setRiskAssessmentAttached] = useState(false);

  // Appendix & Notes
  const [appendixNotes, setAppendixNotes] = useState('');
  const [appendixPhotos, setAppendixPhotos] = useState([]);
  const [appendixFiles, setAppendixFiles] = useState([]);

  // Circuits (basic details)
  const [circuits, setCircuits] = useState({ ...EMPTY_CIRCUITS });
  const [engineerEquipmentOptions, setEngineerEquipmentOptions] = useState({ ...EMPTY_EQUIPMENT_GROUP });
  
  // Certificate-level circuits (max 3) - uses CircuitsManager component
  const [certificateCircuits, setCertificateCircuits] = useState([]);
  const [showCertificateCircuitsManager, setShowCertificateCircuitsManager] = useState(false);
  const engineerEquipmentCacheRef = useRef({});
  const [equipmentLoading, setEquipmentLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsHeaderMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(PRESET_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFieldPresets({
          ...DEFAULT_FIELD_PRESETS,
          ...parsed,
        });
      }
    } catch (error) {
      console.error('Failed to load MEWC presets', error);
    }
  }, []);

  const getFieldValueByKey = useCallback((fieldKey) => {
    switch (fieldKey) {
      case 'extentCovered':
        return extentCovered || '';
      case 'generalCondition':
        return generalCondition || '';
      case 'bs7671Departures':
        return bs7671Departures || '';
      default:
        return '';
    }
  }, [extentCovered, generalCondition, bs7671Departures]);

  const updatePresetsForField = useCallback((fieldKey, updater) => {
    setFieldPresets((prev) => {
      const currentList = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
      const nextList = typeof updater === 'function' ? updater(currentList) : updater;
      const nextState = {
        ...DEFAULT_FIELD_PRESETS,
        ...prev,
        [fieldKey]: nextList,
      };
      try {
        localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(nextState));
      } catch (error) {
        console.error('Failed to persist MEWC presets', error);
      }
      return nextState;
    });
  }, []);

  const handleOpenPresetDrawer = useCallback((fieldKey) => {
    if (!PRESET_FIELD_CONFIG[fieldKey]) return;
    setActivePresetField(fieldKey);
    setPresetForm({
      name: '',
      content: getFieldValueByKey(fieldKey),
    });
    setPresetError('');
    setPresetDrawerOpen(true);
  }, [getFieldValueByKey]);

  const handlePresetDrawerChange = useCallback((open) => {
    setPresetDrawerOpen(open);
    if (!open) {
      setActivePresetField(null);
      setPresetForm({ name: '', content: '' });
      setPresetError('');
    }
  }, []);

  const setFieldValueByKey = useCallback((fieldKey, value) => {
    const nextValue = value ?? '';
    switch (fieldKey) {
      case 'extentCovered':
        setExtentCovered(nextValue);
        break;
      case 'generalCondition':
        setGeneralCondition(nextValue);
        break;
      case 'bs7671Departures':
        setBs7671Departures(nextValue);
        break;
      default:
        break;
    }
  }, []);

  const handleSavePreset = useCallback(() => {
    if (!activePresetField) return;
    const name = presetForm.name.trim();
    const content = presetForm.content.trim();
    if (!name || !content) {
      setPresetError('Please add both a preset name and some text.');
      return;
    }

    updatePresetsForField(activePresetField, (list) => {
      const filtered = list.filter((item) => item.name.toLowerCase() !== name.toLowerCase());
      return [
        {
          id: createPresetId(),
          name,
          content,
          createdAt: new Date().toISOString(),
        },
        ...filtered,
      ];
    });

    setPresetForm({ name: '', content });
    setPresetError('');
  }, [activePresetField, presetForm, updatePresetsForField]);

  const handleDeletePreset = useCallback((fieldKey, presetId) => {
    updatePresetsForField(fieldKey, (list) => list.filter((item) => item.id !== presetId));
  }, [updatePresetsForField]);

  const handleApplyPreset = useCallback((fieldKey, presetValue) => {
    if (!fieldKey) return;
    setFieldValueByKey(fieldKey, presetValue ?? '');
    setPresetError('');
    handlePresetDrawerChange(false);
  }, [handlePresetDrawerChange, setFieldValueByKey]);

  // Draft autosave management
  const formatDateForUid = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  };

  const generateUid = () => {
    const datePart = formatDateForUid(new Date());
    const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `MEWC-${datePart}-${rand}`;
  };

  // Load clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await clientsAPI.getAll({ limit: 100 });
        setClients(response.clients || []);
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSignatories = async () => {
      if (!tenantId) {
        if (isMounted) {
          setSignatoryOptions([]);
          setSignatoriesLoading(false);
        }
        return;
      }

      setSignatoriesLoading(true);
      try {
        const [usersResult, settingsResult] = await Promise.allSettled([
          tenantUsersAPI.list(tenantId),
          api.get('/settings', userRole === 'global_admin' && tenantId ? { params: { tenantId } } : undefined),
        ]);

        if (!isMounted) return;

        const nextOptions = [];

        if (usersResult.status === 'fulfilled') {
          const users = Array.isArray(usersResult.value?.users) ? usersResult.value.users : [];
          users
            .filter((user) => user.isActive !== false)
            .forEach((user) => {
              const nameParts = [user.firstName, user.lastName].filter(Boolean);
              const label = nameParts.length > 0 ? nameParts.join(' ') : (user.email || 'Unnamed user');
              nextOptions.push({
                value: `user:${user.id}`,
                label,
                type: 'user',
                meta: {
                  id: user.id,
                  email: user.email,
                  role: user.role,
                },
              });
            });
          nextOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        } else {
          console.error('Failed to load tenant users', usersResult.reason);
        }

        let tenantLabel = '';
        if (settingsResult.status === 'fulfilled') {
          tenantLabel = settingsResult.value?.data?.settings?.company_name || '';
        } else if (settingsResult.status === 'rejected') {
          console.error('Failed to load tenant settings', settingsResult.reason);
        }

        if (!nextOptions.some((opt) => opt.type === 'tenant')) {
          nextOptions.push({
            value: `tenant:${tenantId}`,
            label: tenantLabel || 'Tenant Account',
            type: 'tenant',
            meta: {
              id: tenantId,
            },
          });
        }

        setSignatoryOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load signatories', error);
          setSignatoryOptions([]);
        }
      } finally {
        if (isMounted) {
          setSignatoriesLoading(false);
        }
      }
    };

    loadSignatories();

    return () => {
      isMounted = false;
    };
  }, [tenantId, userRole]);

  const inspectedByOptions = useMemo(
    () => mergeOptionsWithLegacy(signatoryOptions, inspectedBy),
    [signatoryOptions, inspectedBy]
  );

  const authorisedByOptions = useMemo(
    () => mergeOptionsWithLegacy(signatoryOptions, authorisedBy),
    [signatoryOptions, authorisedBy]
  );

  const handleSignatorySelectChange = useCallback((value, setter, availableOptions) => {
    if (!value) {
      setter(createEmptySignatory());
      return;
    }

    const match = availableOptions.find((opt) => opt.value === value);

    if (match) {
      const optionId = match.meta?.id
        || (match.type === 'tenant' ? tenantId || ''
          : value.startsWith('user:') ? value.split(':')[1] : '');
      setter({
        id: optionId || '',
        label: match.label,
        type: match.type,
        value: match.value,
      });
      return;
    }

    const label = value.startsWith('legacy:') ? value.slice('legacy:'.length) : value;
    setter({
      id: '',
      label,
      type: 'legacy',
      value,
    });
  }, [tenantId]);

  const fetchEquipmentForEngineer = useCallback(async (engineerValue) => {
    if (!tenantId || !engineerValue) {
      setEngineerEquipmentOptions({ ...EMPTY_EQUIPMENT_GROUP });
      return;
    }

    const userId = engineerValue.startsWith('user:') ? engineerValue.split(':')[1] : engineerValue;
    if (!userId) {
      setEngineerEquipmentOptions({ ...EMPTY_EQUIPMENT_GROUP });
      return;
    }

    if (engineerEquipmentCacheRef.current[userId]) {
      setEngineerEquipmentOptions(engineerEquipmentCacheRef.current[userId]);
      return;
    }

    setEquipmentLoading(true);
    try {
      const response = await testEquipmentAPI.getByUser(userId, tenantId);
      const equipmentList = Array.isArray(response?.equipment) ? response.equipment : [];

      const grouped = {
        multifunctionMft: [],
        continuity: [],
        insulationResistance: [],
        earthLoop: [],
        rcd: [],
      };

      equipmentList.forEach((item) => {
        const categoryKey = EQUIPMENT_TYPE_MAP[item.equipmentType];
        if (!categoryKey) return;

        const label = item.model || item.serialNumber || item.name || item.description || 'Equipment';
        const value = item.id ? String(item.id) : label;

        grouped[categoryKey].push({ value, label });
      });

      engineerEquipmentCacheRef.current[userId] = grouped;
      setEngineerEquipmentOptions(grouped);
    } catch (error) {
      console.error('Failed to fetch engineer equipment', error);
      setEngineerEquipmentOptions({ ...EMPTY_EQUIPMENT_GROUP });
    } finally {
      setEquipmentLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    const syncFromOptions = (signatory, setter) => {
      if (!signatory || !signatory.value) return;
      const match = signatoryOptions.find((opt) => opt.value === signatory.value);
      if (!match) return;

      const optionId = match.meta?.id
        || (match.type === 'tenant' ? tenantId || '' : signatory.id || '');

      if (match.label === signatory.label && (optionId || '') === (signatory.id || '')) {
        return;
      }

      setter({
        id: optionId || '',
        label: match.label,
        type: match.type,
        value: match.value,
      });
    };

    syncFromOptions(inspectedBy, setInspectedBy);
    syncFromOptions(authorisedBy, setAuthorisedBy);
  }, [signatoryOptions, inspectedBy, authorisedBy, tenantId]);

  useEffect(() => {
    const engineerValue = inspectedBy?.value || '';
    setCircuits((prev) => ({
      ...prev,
      multifunctionMft: '',
      continuity: '',
      insulationResistance: '',
      earthLoop: '',
      rcd: '',
    }));

    if (engineerValue && engineerValue.startsWith('user:')) {
      fetchEquipmentForEngineer(engineerValue);
    } else {
      setEngineerEquipmentOptions({ ...EMPTY_EQUIPMENT_GROUP });
    }
  }, [inspectedBy?.value, fetchEquipmentForEngineer]);

  // Load or create draft on mount
  useEffect(() => {
    if (isEditMode) {
      // Load existing certificate for editing
      (async () => {
        try {
          setLoading(true);
          const cert = await certificatesAPI.getOne(editId);
          setUid(cert.uid || cert.certificateNum || '');
          setClientId(cert.client?.id || cert.clientId || '');
          setSelectedClient(cert.client || null);
          const dateCreatedValue = cert.dateCreated 
            ? (cert.dateCreated.includes('T') ? cert.dateCreated.split('T')[0] : cert.dateCreated)
            : new Date().toISOString().slice(0, 10);
          setDateCreated(dateCreatedValue);
          setInstallationAddress(cert.installationAddress || '');
          setPremises(cert.installationType || '');
          const d = cert.data || {};
          setUseClientAddress(!!d.useClientAddress);
          setExtentCovered(d.extentCovered || '');
          setMinorWorksDate(d.minorWorksDate || new Date().toISOString().slice(0, 10));
          setEarthingArrangement(d.earthingArrangement || '');
          setGeneralCondition(d.generalCondition || '');
          setInspectedBy(parseSignatory(d.inspectedBy, tenantId || ''));
          const inspectedDateValue = d.inspectedDate 
            ? (d.inspectedDate.includes('T') ? d.inspectedDate.split('T')[0] : d.inspectedDate)
            : new Date().toISOString().slice(0, 10);
          setInspectedDate(inspectedDateValue);
          setAuthorisedBy(parseSignatory(d.authorisedBy, tenantId || ''));
          const authorisedDateValue = d.authorisedDate 
            ? (d.authorisedDate.includes('T') ? d.authorisedDate.split('T')[0] : d.authorisedDate)
            : '';
          setAuthorisedDate(authorisedDateValue);
          setBs7671Departures(d.bs7671Departures || '');
          setRiskAssessmentAttached(!!d.riskAssessmentAttached);
          setCertificateCircuits(Array.isArray(d.certificateCircuits) ? d.certificateCircuits : []);
          setCircuits(
            d.circuits && typeof d.circuits === 'object' && !Array.isArray(d.circuits)
              ? {
                  name: d.circuits.name || '',
                  location: d.circuits.location || '',
                  zsAtDb: d.circuits.zsAtDb || '',
                  ipfAtDb: d.circuits.ipfAtDb || '',
                  spdStatus: d.circuits.spdStatus || '',
                  notes: d.circuits.notes || '',
                  multifunctionMft: d.circuits.multifunctionMft || '',
                  continuity: d.circuits.continuity || '',
                  insulationResistance: d.circuits.insulationResistance || '',
                  earthLoop: d.circuits.earthLoop || '',
                  rcd: d.circuits.rcd || '',
                }
              : { ...EMPTY_CIRCUITS }
          );
          setAppendixNotes(d.appendixNotes || '');
          setAppendixPhotos(Array.isArray(d.appendixPhotos) ? d.appendixPhotos : []);
          setAppendixFiles(Array.isArray(d.appendixFiles) ? d.appendixFiles : []);
          setServerId(cert.id);
          setUidLocked(true);
          setCertificateStatus(cert.status || 'draft');
        } catch (err) {
          console.error('Failed to load certificate', err);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    const currentUid = localStorage.getItem('mewcCurrentDraftUid');
    if (currentUid) {
      const raw = localStorage.getItem(`mewcDraft:${currentUid}`);
      if (raw) {
        try {
          const draft = JSON.parse(raw);
          setUid(draft.uid || currentUid);
          setClientId(draft.clientId || '');
          setSelectedClient(draft.selectedClient || null);
          setDateCreated(draft.dateCreated || new Date().toISOString().slice(0, 10));
          setInstallationAddress(draft.installationAddress || '');
          setUseClientAddress(!!draft.useClientAddress);
          setPremises(draft.premises || '');
          setExtentCovered(draft.extentCovered || '');
          setMinorWorksDate(draft.minorWorksDate || new Date().toISOString().slice(0, 10));
          setEarthingArrangement(draft.earthingArrangement || '');
          setGeneralCondition(draft.generalCondition || '');
          setInspectedBy(parseSignatory(draft.inspectedBy, tenantId || ''));
          setInspectedDate(draft.inspectedDate || new Date().toISOString().slice(0, 10));
          setAuthorisedBy(parseSignatory(draft.authorisedBy, tenantId || ''));
          setAuthorisedDate(draft.authorisedDate || '');
          setBs7671Departures(draft.bs7671Departures || '');
          setRiskAssessmentAttached(!!draft.riskAssessmentAttached);
          setCertificateCircuits(Array.isArray(draft.certificateCircuits) ? draft.certificateCircuits : []);
          setCircuits(
            draft.circuits && typeof draft.circuits === 'object' && !Array.isArray(draft.circuits)
              ? {
                  name: draft.circuits.name || '',
                  location: draft.circuits.location || '',
                  zsAtDb: draft.circuits.zsAtDb || '',
                  ipfAtDb: draft.circuits.ipfAtDb || '',
                  spdStatus: draft.circuits.spdStatus || '',
                  notes: draft.circuits.notes || '',
                  multifunctionMft: draft.circuits.multifunctionMft || '',
                  continuity: draft.circuits.continuity || '',
                  insulationResistance: draft.circuits.insulationResistance || '',
                  earthLoop: draft.circuits.earthLoop || '',
                  rcd: draft.circuits.rcd || '',
                }
              : { ...EMPTY_CIRCUITS }
          );
          setAppendixNotes(draft.appendixNotes || '');
          setAppendixPhotos(Array.isArray(draft.appendixPhotos) ? draft.appendixPhotos : []);
          setAppendixFiles(Array.isArray(draft.appendixFiles) ? draft.appendixFiles : []);
          setCertificateStatus(draft.status || 'draft');
          return;
        } catch (_) {
          // fall through to create fresh draft
        }
      }
    }
    const newUid = generateUid();
    setUid(newUid);
    localStorage.setItem('mewcCurrentDraftUid', newUid);
    setTimeout(() => saveDraft(newUid), 0);
  }, []);

  // Helper to remove base64 data from images
  const sanitizeImage = (img) => {
    if (!img) return img;
    const { dataUrl, ...rest } = img;
    return rest;
  };

  const sanitizeImages = (images) => {
    if (!Array.isArray(images)) return images;
    return images.map(sanitizeImage);
  };

  // Build draft
  const buildDraft = (uidValue = uid, forServer = false, statusValue = certificateStatus) => {
    const draft = {
      uid: uidValue,
      clientId,
      selectedClient: forServer ? null : selectedClient,
      status: statusValue,
      dateCreated,
      installationAddress,
      useClientAddress,
      premises,
      minorWorksDate,
      earthingArrangement,
      extentCovered,
      generalCondition,
      inspectedBy: serializeSignatory(inspectedBy),
      inspectedDate,
      authorisedBy: serializeSignatory(authorisedBy),
      authorisedDate,
      bs7671Departures,
      riskAssessmentAttached,
      circuits,
      certificateCircuits, // Certificate-level circuits (max 3) - uses CircuitsManager component
      appendixNotes,
      lastSavedAt: new Date().toISOString(),
    };

    if (forServer) {
      draft.appendixPhotos = sanitizeImages(appendixPhotos);
      draft.appendixFiles = sanitizeImages(appendixFiles);
    } else {
      draft.appendixPhotos = appendixPhotos;
      draft.appendixFiles = appendixFiles;
    }

    return draft;
  };

  const saveDraft = useCallback((uidValue = uid, statusValue = certificateStatus) => {
    const draft = buildDraft(uidValue, false, statusValue);
    const key = `mewcDraft:${uidValue || uid}`;
    try {
      localStorage.setItem(key, JSON.stringify(draft));
      localStorage.setItem('mewcCurrentDraftUid', uidValue || uid);
    } catch (error) {
      console.error('Failed to save draft', error);
    }
  }, [uid, clientId, selectedClient, certificateStatus, dateCreated, installationAddress, useClientAddress, premises, minorWorksDate, earthingArrangement, extentCovered, generalCondition, inspectedBy, inspectedDate, authorisedBy, authorisedDate, bs7671Departures, riskAssessmentAttached, circuits, appendixNotes, appendixPhotos, appendixFiles]);

  const persistServerDraft = useCallback(async () => {
    if (!clientId || !uid) return;
    try {
      const draft = buildDraft(uid, true, certificateStatus);
      const payload = {
        certificateType: 'mewc',
        uid,
        clientId,
        installationAddress,
        installationType: premises,
        status: certificateStatus,
        data: draft,
      };

      if (serverId) {
        await certificatesAPI.update(serverId, payload);
      } else {
        const result = await certificatesAPI.create(payload);
        if (result?.id) {
          setServerId(result.id);
          setUidLocked(true);
        }
      }
    } catch (error) {
      console.error('Failed to persist server draft', error);
    }
  }, [uid, clientId, installationAddress, premises, certificateStatus, serverId, buildDraft]);

  useEffect(() => {
    if (!clientId) return;
    const id = setTimeout(() => { persistServerDraft(); }, 1000);
    return () => clearTimeout(id);
  }, [
    clientId,
    uid,
    dateCreated,
    installationAddress,
    useClientAddress,
    premises,
    extentCovered,
    minorWorksDate,
    earthingArrangement,
    generalCondition,
    inspectedBy,
    inspectedDate,
    authorisedBy,
    authorisedDate,
    bs7671Departures,
    riskAssessmentAttached,
    JSON.stringify(circuits),
    appendixNotes,
    appendixPhotos,
    appendixFiles,
  ]);

  const handleChangeTab = (key) => {
    saveDraft();
    setActiveTab(key);
  };

  const getClientDisplayName = (client) => {
    if (!client) return '';
    if (client.client_type === 'company') {
      return client.company_name || 'Unnamed Company';
    }
    const name = `${client.first_name || ''} ${client.last_name || ''}`.trim();
    return name || 'Unnamed Individual';
  };

  const clientDisplayName = useMemo(() => {
    return getClientDisplayName(selectedClient);
  }, [selectedClient]);

  const clientAddressString = useMemo(() => {
    if (!selectedClient) return '';
    const parts = [
      selectedClient.address_line1,
      selectedClient.address_line2,
      selectedClient.city,
      selectedClient.county,
      selectedClient.postcode,
      selectedClient.country,
    ].filter(Boolean);
    return parts.join(', ');
  }, [selectedClient]);

  const handleCircuitChange = (field, value) => {
    setCircuits((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Certificate-level circuits handlers (max 3) - uses CircuitsManager component
  const handleOpenCertificateCircuitsManager = () => {
    setShowCertificateCircuitsManager(true);
  };

  const handleCloseCertificateCircuitsManager = () => {
    setShowCertificateCircuitsManager(false);
  };

  const handleSaveCertificateCircuits = (circuits) => {
    setCertificateCircuits(circuits);
    saveDraft();
    persistServerDraft();
  };

  const renderEquipmentSelectOptions = (category) => {
    const engineerSelected = inspectedBy?.value && inspectedBy.value.startsWith('user:');
    if (!engineerSelected) {
      return (
        <option key={`${category}-select-engineer`} value="" disabled>
          Select engineer to load equipment
        </option>
      );
    }

    const options = engineerEquipmentOptions[category] || [];

    if (equipmentLoading) {
      return (
        <option key={`${category}-loading`} value="" disabled>
          Loading equipment...
        </option>
      );
    }

    if (options.length === 0) {
      return (
        <option key={`${category}-none`} value="N/A">
          N/A
        </option>
      );
    }

    return options.map((option) => (
      <option key={`${category}-${option.value}`} value={option.value}>
        {option.label}
      </option>
    ));
  };

  const handleAppendixFiles = async (files) => {
    const fileArr = Array.from(files || []);
    const reads = await Promise.all(
      fileArr.map((file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              id:
                crypto && crypto.randomUUID
                  ? crypto.randomUUID()
                  : Math.random().toString(36).slice(2),
              name: file.name,
              type: file.type,
              size: file.size,
              dataUrl: reader.result,
              title: '',
              caption: '',
            });
          reader.readAsDataURL(file);
        })
      )
    );
    setAppendixPhotos((prev) => [...prev, ...reads]);
  };

  const moveAppendixPhoto = (id, direction) => {
    setAppendixPhotos((prev) => {
      const index = prev.findIndex((p) => p.id === id);
      if (index < 0) return prev;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(target, 0, removed);
      return next;
    });
  };

  const handleAppendixFileUpload = async (files) => {
    const fileArr = Array.from(files || []);
    
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    const validFiles = fileArr.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(file.type) || allowedExtensions.includes(ext);
    });

    if (validFiles.length !== fileArr.length) {
      showError('Invalid file type', 'Some files were not uploaded. Only PDFs, Microsoft Office documents, and images are allowed.');
    }

    if (validFiles.length === 0) return;

    const reads = await Promise.all(
      validFiles.map((file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            resolve({
              id:
                crypto && crypto.randomUUID
                  ? crypto.randomUUID()
                  : Math.random().toString(36).slice(2),
              name: file.name,
              type: file.type,
              size: file.size,
              extension: ext,
              dataUrl: reader.result,
              description: '',
            });
          };
          reader.readAsDataURL(file);
        })
      )
    );
    setAppendixFiles((prev) => [...prev, ...reads]);
  };

  const moveAppendixFile = (id, direction) => {
    setAppendixFiles((prev) => {
      const index = prev.findIndex((f) => f.id === id);
      if (index < 0) return prev;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(target, 0, removed);
      return next;
    });
  };

  const getFileIcon = (extension) => {
    const ext = extension.toLowerCase();
    if (ext === '.pdf') return '📄';
    if (['.doc', '.docx'].includes(ext)) return '📝';
    if (['.xls', '.xlsx'].includes(ext)) return '📊';
    if (['.ppt', '.pptx'].includes(ext)) return '📽️';
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return '🖼️';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUseClientAddressToggle = (checked) => {
    setUseClientAddress(checked);
    if (checked) {
      setInstallationAddress(clientAddressString);
    }
  };

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      setPdfError('');
      const draft = buildDraft(uid, true, certificateStatus);
      const response = await certificatesAPI.generatePdf(serverId || 'draft', {
        certificateType: 'mewc',
        ...draft,
      });
      if (response?.pdfUrl) {
        setPdfUrl(response.pdfUrl);
        setShowPdfModal(true);
      } else {
        setPdfError('Failed to generate PDF');
      }
    } catch (error) {
      setPdfError(error?.message || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const activePresetLabel = activePresetField ? PRESET_FIELD_CONFIG[activePresetField]?.label : '';
  const presetsForActiveField = activePresetField ? fieldPresets[activePresetField] || [] : [];

  // Show Certificate CircuitsManager if flag is set (max 3 circuits)
  if (showCertificateCircuitsManager) {
    return (
      <CircuitsManager
        boardData={{ circuits: certificateCircuits, name: 'Certificate Circuits' }}
        onSave={handleSaveCertificateCircuits}
        onBack={handleCloseCertificateCircuitsManager}
        maxCircuits={3}
      />
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading certificate...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Minor Electrical Works Certificate</h1>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minor Electrical Works Certificate</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Requirements for electrical installations (BS 7671-2018+A3:2024 IET Wiring Regulations)</p>
            </div>
            <div className="w-full md:w-auto">
              <Input
                type="text"
                label="Certificate Unique ID"
                value={uid}
                onChange={(e) => {
                  const newUid = e.target.value;
                  setUid(newUid);
                  saveDraft(newUid);
                }}
                disabled={uidLocked}
                className="h-8 w-full md:w-[360px]"
                placeholder={`MEWC-${formatDateForUid(new Date())}-XXXXXX`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{uidLocked ? 'UID locked after first save.' : `Editable. Suggested: MEWC-${formatDateForUid(new Date())}-XXXXXX`}</p>
            </div>
            <div ref={headerMenuRef} className="relative self-end md:self-start">
              <button
                type="button"
                onClick={() => setIsHeaderMenuOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={isHeaderMenuOpen}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                <Menu className="h-4 w-4" />
                Menu
              </button>
              {isHeaderMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 z-30">
                  <button
                    type="button"
                    onClick={handleOpenStatusModal}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-xs text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Change status
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    disabled={pdfLoading}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-xs text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    {pdfLoading ? 'Generating PDF...' : 'Export certificate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsHeaderMenuOpen(false)}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-xs text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tenant/certification')}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Exit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-2 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleChangeTab(key)}
                className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border transition-colors
                  ${activeTab === key
                    ? 'bg-white dark:bg-gray-800 border-orange-400 dark:border-orange-500 shadow text-gray-900 dark:text-gray-100'
                    : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'}
                `}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium text-center">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          {activeTab === 'installation_details' && (
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Client & Installation</h2>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Details of client or person ordering report */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 h-full">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Details of client or person ordering report</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Client lookup */}
                    <div>
                      <div className="flex gap-2">
                        <Select
                          label="Client"
                          value={clientId}
                          onChange={(e) => {
                            const client = clients.find(c => c.id === e.target.value);
                            setSelectedClient(client || null);
                            setClientId(e.target.value);
                            if (useClientAddress && client) {
                              const parts = [client.address_line1, client.address_line2, client.city, client.county, client.postcode, client.country].filter(Boolean);
                              setInstallationAddress(parts.join(', '));
                            }
                            saveDraft();
                          }}
                          className="h-8 flex-1"
                        >
                          <option value="">Select client...</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>
                              {getClientDisplayName(client)}
                            </option>
                          ))}
                        </Select>
                        <button
                          type="button"
                          onClick={() => setShowAddClientModal(true)}
                          className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                          title="Add new client"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      {selectedClient && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate" title={clientAddressString}>{clientAddressString || 'No address on file'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details of the installation */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 h-full">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Details of the installation</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Textarea
                        label="Installation Address"
                        rows={3}
                        value={installationAddress}
                        onChange={(e) => setInstallationAddress(e.target.value)}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          id="use-client-address"
                          type="checkbox"
                          checked={useClientAddress}
                          onChange={(e) => handleUseClientAddressToggle(e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <label htmlFor="use-client-address" className="text-xs text-gray-700 dark:text-gray-300">Use client address</label>
                      </div>
                      <div className="mt-4">
                        <FormLabel htmlFor="description-of-premises">Description of premises</FormLabel>
                        <TextInputWithSuggestions
                          id="description-of-premises"
                          name="description_of_premises"
                          value={premises}
                          onChange={(e) => setPremises(e.target.value)}
                          options={PREMISES_OPTIONS}
                          placeholder="Select or type description…"
                          showAllOnFocus
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extent and limitations of inspection and testing */}
              <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Description of the minor works</h3>
                <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <FormLabel>Description of the minor works</FormLabel>
                      <button
                        type="button"
                        onClick={() => handleOpenPresetDrawer('extentCovered')}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Presets
                      </button>
                    </div>
                    <Textarea rows={3} value={extentCovered} onChange={(e) => setExtentCovered(e.target.value)} placeholder="Describe the extent of the installation..." />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <FormLabel>Date minor works completed</FormLabel>
                        <button
                          type="button"
                          onClick={() => setMinorWorksDate(new Date().toISOString().slice(0, 10))}
                          className="h-8 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-md px-2"
                        >
                          Today
                        </button>
                      </div>
                      <Input
                        type="date"
                        value={minorWorksDate}
                        onChange={(e) => setMinorWorksDate(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 space-y-4">
                      <div>
                        <FormLabel>Earthing arrangement</FormLabel>
                        <div className="inline-flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                          {['TN-S','TN-C-S','TN-C','TT','IT'].map((v, idx, arr) => {
                            const isSelected = earthingArrangement === v;
                            const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                            const classes = isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                              : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setEarthingArrangement(v)}
                                className={`px-3 py-2 text-xs inline-flex items-center justify-center min-w-[58px] border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <FormLabel>Comments on the existing installation (Including any defects observed)</FormLabel>
                      <button
                        type="button"
                        onClick={() => handleOpenPresetDrawer('generalCondition')}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Presets
                      </button>
                    </div>
                    <Textarea rows={5} value={generalCondition} onChange={(e) => setGeneralCondition(e.target.value)} placeholder="Describe the general condition..." />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">See Regulation 644.1.2</p>
                  </div>
                </div>
              </div>

              {/* BS 7671 Departures and Permitted Exceptions */}
              <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Details of departures from BS 7671, as amended</h3>
                      <button
                        type="button"
                        onClick={() => handleOpenPresetDrawer('bs7671Departures')}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Presets
                      </button>
                    </div>
                    <Textarea
                      rows={6}
                      value={bs7671Departures}
                      onChange={(e) => setBs7671Departures(e.target.value)}
                      placeholder="Enter details of any departures from BS 7671..."
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Regulations 120.3, 133.1.3, 133.5, 411.3.3. Where applicable, a suitable risk assessment(s) must be attached to this certificate.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="risk-assessment-attached"
                      type="checkbox"
                      checked={riskAssessmentAttached}
                      onChange={(e) => {
                        setRiskAssessmentAttached(e.target.checked);
                        saveDraft();
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="risk-assessment-attached" className="text-xs text-gray-700 dark:text-gray-300">Risk assessment attached</label>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => { saveDraft(); persistServerDraft(); handleChangeTab('circuits'); }}
                  className="h-8 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next: Circuits →
                </button>
              </div>
            </section>
          )}

          {activeTab === 'circuits' && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Circuits</h2>
                <button
                  type="button"
                  onClick={handleOpenCertificateCircuitsManager}
                  className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Circuits
                </button>
              </div>
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="text"
                      label="Board / Circuit Name"
                      value={circuits.name}
                      onChange={(e) => handleCircuitChange('name', e.target.value)}
                      className="h-8"
                      placeholder="e.g. Kitchen ring main"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      label="Location"
                      value={circuits.location}
                      onChange={(e) => handleCircuitChange('location', e.target.value)}
                      className="h-8"
                      placeholder="Distribution board location"
                    />
                  </div>
                </div>

                <hr className="border-gray-200 dark:border-gray-700" />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Input
                      type="text"
                      label="Zs at DB (Ω)"
                      value={circuits.zsAtDb}
                      onChange={(e) => handleCircuitChange('zsAtDb', e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      label="IPF at DB (kA)"
                      value={circuits.ipfAtDb}
                      onChange={(e) => handleCircuitChange('ipfAtDb', e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Select
                      label="SPD functionality confirmed"
                      value={circuits.spdStatus}
                      onChange={(e) => handleCircuitChange('spdStatus', e.target.value)}
                      className="h-8"
                    >
                      <option value="">Select result…</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                      <option value="LIM">LIM</option>
                      <option value="N/A">N/A</option>
                    </Select>
                  </div>
                </div>

                <div>
                  <Textarea
                    label="Notes"
                    rows={4}
                    value={circuits.notes}
                    onChange={(e) => handleCircuitChange('notes', e.target.value)}
                    placeholder="Add any circuit-specific notes"
                  />
                </div>
              </div>

              <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Equipment</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'multifunctionMft', label: 'Multifunction (MFT)' },
                    { key: 'continuity', label: 'Continuity' },
                    { key: 'insulationResistance', label: 'Insulation resistance' },
                    { key: 'earthLoop', label: 'Earth loop' },
                    { key: 'rcd', label: 'RCD' },
                  ].map((item) => (
                    <div key={item.key}>
                      <Select
                        label={item.label}
                        value={circuits[item.key]}
                        onChange={(e) => handleCircuitChange(item.key, e.target.value)}
                        className="h-8"
                      >
                        <option value="">Select equipment...</option>
                        {renderEquipmentSelectOptions(item.key)}
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('installation_details')} className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Installation Details</button>
                <button type="button" onClick={() => { saveDraft(); persistServerDraft(); handleChangeTab('declaration'); }} className="h-8 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700">Next: Declaration →</button>
              </div>
            </section>
          )}

          {activeTab === 'declaration' && (
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Declaration</h2>

              {/* Declaration */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Declaration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Select
                      label="Inspected & tested by"
                      value={inspectedBy.value}
                      onChange={(e) => handleSignatorySelectChange(e.target.value, setInspectedBy, inspectedByOptions)}
                      className="h-8"
                    >
                      <option value="">{signatoriesLoading ? 'Loading team…' : 'Select engineer…'}</option>
                      {inspectedByOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FormLabel>Date</FormLabel>
                    <div className="flex flex-col gap-2">
                      <Input type="date" value={inspectedDate} onChange={(e) => setInspectedDate(e.target.value)} className="h-8" />
                      <button
                        type="button"
                        onClick={() => setInspectedDate(new Date().toISOString().slice(0, 10))}
                        className="h-8 inline-flex items-center self-start px-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Today
                      </button>
                    </div>
                  </div>
                  <div>
                    <Select
                      label="Report authorised for issue by"
                      value={authorisedBy.value}
                      onChange={(e) => handleSignatorySelectChange(e.target.value, setAuthorisedBy, authorisedByOptions)}
                      className="h-8"
                    >
                      <option value="">{signatoriesLoading ? 'Loading team…' : 'Select authoriser…'}</option>
                      {authorisedByOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FormLabel>Date</FormLabel>
                    <div className="flex flex-col gap-2">
                      <Input type="date" value={authorisedDate} onChange={(e) => setAuthorisedDate(e.target.value)} className="h-8" />
                      <button
                        type="button"
                        onClick={() => setAuthorisedDate(new Date().toISOString().slice(0, 10))}
                        className="h-8 inline-flex items-center self-start px-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Today
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Only QS and Admin roles can sign off reports.</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('circuits')} className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Circuits</button>
                <button type="button" onClick={() => { saveDraft(); persistServerDraft(); handleChangeTab('appendix'); }} className="h-8 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700">Next: Appendix →</button>
              </div>
            </section>
          )}

          {activeTab === 'appendix' && (
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Appendix & Notes</h2>
              {/* Additional Page */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-6">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Additional Page</h3>
                </div>
                <div className="p-4">
                  <Textarea
                    label="Any additional content"
                    rows={4}
                    value={appendixNotes}
                    onChange={(e) => setAppendixNotes(e.target.value)}
                    placeholder="Enter any additional information to appear at the end of the certificate..."
                    className="text-xs"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This text will appear on an additional page at the end of the certificate, above any appendix photos.</p>
                </div>
              </div>

              {/* Appendix Photos */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Appendix Photos</h3>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                    + Upload Photo
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleAppendixFiles(e.target.files)}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="p-4">
                  {appendixPhotos.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-12 text-center">
                      <div className="text-5xl mb-2 text-gray-400">🖼️</div>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">No photos added yet</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload your first photo to get started</div>
                      <div className="mt-3">
                        <label className="inline-flex items-center px-3 py-2 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          Upload Photo
                          <input type="file" accept="image/*" multiple onChange={(e) => handleAppendixFiles(e.target.files)} className="hidden" />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {appendixPhotos.map((p, idx) => (
                        <div key={p.id} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden bg-white dark:bg-gray-800">
                          <div className="relative">
                            <img src={p.dataUrl} alt={p.name} className="h-48 w-full object-cover" />
                            <div className="absolute top-2 right-2 flex gap-1">
                              <button onClick={() => setAppendixPhotos(prev => prev.filter(x => x.id !== p.id))} className="px-2 py-1 text-xs rounded bg-white/90 text-red-600">✕</button>
                            </div>
                          </div>
                          <div className="p-3 space-y-2">
                            <Input
                              type="text"
                              value={p.title || ''}
                              onChange={(e) => setAppendixPhotos(prev => prev.map(x => x.id === p.id ? { ...x, title: e.target.value } : x))}
                              placeholder="Title (optional)"
                              className="h-8"
                            />
                            <Input
                              type="text"
                              value={p.caption || ''}
                              onChange={(e) => setAppendixPhotos(prev => prev.map(x => x.id === p.id ? { ...x, caption: e.target.value } : x))}
                              placeholder="Caption (optional)"
                              className="h-8"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={p.name}>{p.name}</span>
                              <div className="flex gap-2">
                                <button onClick={() => moveAppendixPhoto(p.id, 'up')} className="h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">↑</button>
                                <button onClick={() => moveAppendixPhoto(p.id, 'down')} className="h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">↓</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Appendix Files */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mt-6">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Appendix Files</h3>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                    + Upload Files
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                      multiple
                      onChange={(e) => handleAppendixFileUpload(e.target.files)}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="p-4">
                  {appendixFiles.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-12 text-center">
                      <div className="text-5xl mb-2 text-gray-400">📎</div>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">No files added yet</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload PDFs, Microsoft Office documents, or images</div>
                      <div className="mt-3">
                        <label className="inline-flex items-center px-3 py-2 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          Upload Files
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                            multiple
                            onChange={(e) => handleAppendixFileUpload(e.target.files)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appendixFiles.map((file, idx) => (
                        <div key={file.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800 flex items-start gap-4">
                          <div className="text-4xl flex-shrink-0">{getFileIcon(file.extension)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={file.name}>
                                  {file.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {formatFileSize(file.size)} • {file.extension.toUpperCase()}
                                </div>
                              </div>
                              <button
                                onClick={() => setAppendixFiles(prev => prev.filter(f => f.id !== file.id))}
                                className="px-2 py-1 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex-shrink-0"
                                title="Remove file"
                              >
                                ✕
                              </button>
                            </div>
                            <Input
                              type="text"
                              value={file.description || ''}
                              onChange={(e) => setAppendixFiles(prev => prev.map(f => f.id === file.id ? { ...f, description: e.target.value } : f))}
                              placeholder="Description (optional)"
                              className="h-8"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => moveAppendixFile(file.id, 'up')}
                                disabled={idx === 0}
                                className={`h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${
                                  idx === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveAppendixFile(file.id, 'down')}
                                disabled={idx === appendixFiles.length - 1}
                                className={`h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${
                                  idx === appendixFiles.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                ↓
                              </button>
                              {file.dataUrl && (
                                <a
                                  href={file.dataUrl}
                                  download={file.name}
                                  className="h-8 inline-flex items-center px-2 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                  Download
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('declaration')} className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Declaration</button>
                <button type="button" onClick={() => { saveDraft(); persistServerDraft(); }} className="h-8 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700">Save Draft</button>
              </div>
            </section>
          )}
        </div>

        {/* Save Error Message */}
        {saveError && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-xs text-red-800 dark:text-red-200">{saveError}</p>
          </div>
        )}

        {/* Status Modal */}
        {isStatusModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Change Certificate Status</h3>
                {statusError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-xs text-red-800 dark:text-red-200">{statusError}</p>
                  </div>
                )}
                <div className="space-y-2">
                  {STATUS_OPTIONS.map((option) => {
                    const Icon = option.Icon;
                    const isSelected = option.value === certificateStatus;
                    const isDisabled = option.requiresPermission && !canManageRestrictedStatuses;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => !isDisabled && handleStatusSelect(option.value)}
                        disabled={isDisabled || statusUpdating}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${statusUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
                          </div>
                          {isSelected && (
                            <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCloseStatusModal}
                    disabled={statusUpdating}
                    className="h-8 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Client Picker Modal */}
        {showAddClientModal && (
          <ClientPicker
            onSelect={(client) => {
              setSelectedClient(client);
              setClientId(client.id);
              if (useClientAddress && client) {
                const parts = [client.address_line1, client.address_line2, client.city, client.county, client.postcode, client.country].filter(Boolean);
                setInstallationAddress(parts.join(', '));
              }
              setShowAddClientModal(false);
              saveDraft();
            }}
            onClose={() => setShowAddClientModal(false)}
          />
        )}

        {/* PDF Preview Modal */}
        {showPdfModal && pdfUrl && (
          <PdfPreviewModal
            pdfUrl={pdfUrl}
            onClose={() => {
              setShowPdfModal(false);
              setPdfUrl(null);
            }}
            error={pdfError}
          />
        )}

        <ToastContainer />
      </div>
    </Layout>
  );
}

